import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { format } from 'date-fns';
import type { PriceBreakdownContainer } from '@/EditReservation/types';
import { getBreakdownSourceLabel, hasDiscounts } from '@/utils/priceBreakdown';

export function RateFinderPriceDialogComponent({ 
    priceBreakdown, 
    visible, 
    closed, 
    title, 
    dateRange,
    itemType 
}: RateFinderPriceDialogComponentProps) {
    
    if (!priceBreakdown) return null;

    const formatCurrency = (amount: number) => {
        return amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'THB',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDecimal = (amount: number) => {
        return amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <Modal centered backdrop="static" show={visible} onHide={() => { closed?.(); }} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{title} - Price Breakdown</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <table className="table">
                    <tbody>
                        <tr>
                            <th colSpan={2}>
                                <div className="mb-2 text-lg">
                                    <div>{title}</div>
                                    <div>{dateRange}</div>
                                </div>
                            </th>
                        </tr>
                        
                        {/* Base Amount */}
                        <tr>
                            <td>{getBreakdownSourceLabel(priceBreakdown) || 'Base Rate'}</td>
                            <td className="text-right">
                                {formatCurrency(priceBreakdown.BaseAmount.Amount.Gross)}
                            </td>
                        </tr>

                        {/* Multipliers */}
                        {priceBreakdown.Multipliers.map((multiplier, index) => (
                            <tr key={`multiplier-${index}`}>
                                <td>
                                    {multiplier.Source} ({((multiplier.Multiplier || 1) * 100).toFixed(0)}%)
                                </td>
                                <td className="text-right">
                                    {formatCurrency(multiplier.AdjustmentAmount.Gross)}
                                </td>
                            </tr>
                        ))}

                        {/* Discounts */}
                        {hasDiscounts(priceBreakdown) && priceBreakdown.Discounts.map((discount, index) => (
                            <tr key={`discount-${index}`}>
                                <td>
                                    {discount.Source} ({((discount.Multiplier || 1) * 100).toFixed(0)}%)
                                </td>
                                <td className="text-right text-success">
                                    {formatCurrency(discount.AdjustmentAmount.Gross)}
                                </td>
                            </tr>
                        ))}

                        {/* Total */}
                        <tr className="font-bold border-top">
                            <td><strong>Total</strong></td>
                            <td className="text-right">
                                <strong>
                                    {formatCurrency(priceBreakdown.AmountAfterDiscount.Gross)}
                                </strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Modal.Body>
        </Modal>
    );
}

export interface RateFinderPriceDialogComponentProps {
    priceBreakdown: PriceBreakdownContainer | null;
    visible: boolean;
    closed?: () => void;
    title: string;
    dateRange: string;
    itemType: 'room' | 'program';
}